import React, { useCallback, useMemo } from 'react';

import {
  Table,
  TableHeader,
  TableHeaderCell,
  TableHeaderRow,
  TableBody,
  Text,
  Skeleton,
} from '@razorpay/blade/components';

import { AssignedFlexibleComponentsTableDataType } from '../../types';
import { AllocateFBPTableRow } from './AllocateFBPTableRow';

export const FBPAllocationTable = ({
  flexibleData,
  setSelectedRows,
  monthlyDeclaration,
  selectedTiers,
  onTierChange,
  onChange,
  isFetching,
  isLoading,
  selectedRows,
  monthlyDeclarationErrors,
  isAdmin,
}: {
  flexibleData?: AssignedFlexibleComponentsTableDataType[];
  setSelectedRows: (selectedRows: string[]) => void;
  monthlyDeclaration: Record<string, string>;
  selectedTiers?: Record<string, string>;
  onTierChange?: ({ id, tierId }: { id: string; tierId: string }) => void;
  onChange: ({ id, value }: { id: string; value: string }) => void;
  isFetching: boolean;
  isLoading: boolean;
  selectedRows: string[];
  monthlyDeclarationErrors: Record<string, string>;
  isAdmin: boolean;
}) => {
  const handleSelectionChange = useCallback(
    ({ selectedIds }: { selectedIds: (string | number)[] }) => {
      const ids = selectedIds.map((id) => id.toString());
      setSelectedRows(ids);
    },
    [],
  );

  const tableData = flexibleData ?? [];

  const showTierColumn = tableData.some((row) => (row.tiers?.length ?? 0) > 0);

  return (
    <>
      {isLoading ? (
        <Skeleton
          width={'100%'}
          height={'400px'}
          borderRadius="medium"
          marginTop={'spacing.7'}
          testID="FBP-loading-table"
        />
      ) : (
        <Table
          showBorderedCells={true}
          selectionType={isAdmin ? 'multiple' : 'none'}
          defaultSelectedIds={selectedRows}
          data={{ nodes: tableData }}
          display="grid"
          gridTemplateColumns={
            showTierColumn
              ? isAdmin
                ? '0.25fr 3fr 2fr 2fr 2fr 2.5fr 2fr'
                : '3fr 1.5fr 2fr 2fr 2.5fr 2fr'
              : isAdmin
              ? '0.25fr 3fr 2fr 2fr 2.5fr 2fr'
              : '3fr 1.5fr 2fr 2.5fr 2fr'
          }
          rowDensity="normal"
          marginTop={'spacing.7'}
          isRefreshing={isFetching}
          multiSelectTrigger="checkbox"
          onSelectionChange={handleSelectionChange}>
          {(tableData) => (
            <>
              <TableHeader>
                <TableHeaderRow>
                  <TableHeaderCell>
                    <Text weight="semibold"> Flexible component </Text>
                  </TableHeaderCell>
                  <TableHeaderCell>
                    <Text weight="semibold"> Tax Exemption </Text>
                  </TableHeaderCell>
                  {showTierColumn && (
                    <TableHeaderCell>
                      <Text weight="semibold"> Tier </Text>
                    </TableHeaderCell>
                  )}
                  <TableHeaderCell>
                    <Text weight="semibold"> Max. limit per month </Text>
                  </TableHeaderCell>
                  <TableHeaderCell>
                    <Text weight="semibold">Monthly declaration </Text>
                  </TableHeaderCell>
                  <TableHeaderCell>
                    <Text weight="semibold">Yearly amount </Text>
                  </TableHeaderCell>
                </TableHeaderRow>
              </TableHeader>

              <TableBody>
                {tableData.map((rowData) => (
                  <AllocateFBPTableRow
                    key={rowData.id}
                    rowData={rowData}
                    monthlyDeclarationValue={monthlyDeclaration[rowData.id] || ''}
                    onChange={onChange}
                    monthlyDeclarationErrors={monthlyDeclarationErrors}
                    isAdmin={isAdmin}
                    showTierColumn={showTierColumn}
                    selectedTierId={selectedTiers?.[rowData.id]}
                    onTierChange={onTierChange}
                  />
                ))}
              </TableBody>
            </>
          )}
        </Table>
      )}
    </>
  );
};
